import { useEffect, type ReactNode } from 'react';
import { audio } from '@/audio/AudioService';
import { useT } from '@/i18n/useI18n';

export function PreviewDialog({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const t = useT();

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const close = () => {
    audio.play('ui.tap');
    onClose();
  };

  return (
    <div
      role="presentation"
      onClick={close}
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 600,
        display: 'grid',
        placeItems: 'center',
        padding: 18,
        background: 'rgba(3, 2, 16, 0.82)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(event) => event.stopPropagation()}
        className="card"
        style={{
          width: 'min(520px, 100%)',
          maxHeight: 'min(760px, 92%)',
          overflow: 'auto',
          padding: 16,
          border: '1px solid rgba(155,93,229,0.55)',
          background:
            'linear-gradient(160deg, rgba(31,24,84,0.98), rgba(8,5,28,0.98))',
          boxShadow: '0 24px 70px rgba(0,0,0,0.65)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 14,
          }}
        >
          <div className="display" style={{ color: 'var(--c-cyan)', fontSize: 17 }}>
            {title}
          </div>
          <button
            type="button"
            onClick={close}
            aria-label={t('common.close')}
            style={{
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: 10,
              border: '1px solid var(--hairline)',
              background: 'rgba(255,255,255,0.06)',
              color: 'var(--ink)',
              fontSize: 18,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
